import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", title_format: "", description: "", department: "Sales", default_priority: "normal",
  checklist: "", subtasks: "" };

export default function Templates() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const canManage = user?.role === "admin" || ["warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([api.get("/office/templates"), api.get("/office/meta")]);
    setRows(t.data);
    setMeta(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const create = async () => {
    if (!f.name.trim() || !f.title_format.trim()) return toast.error("Name and title format are required");
    try {
      await api.post("/office/templates", {
        name: f.name, title_format: f.title_format, description: f.description || null,
        department: f.department, default_priority: f.default_priority,
        checklist: f.checklist.split("\n").map((x) => x.trim()).filter(Boolean),
        subtasks: f.subtasks.split("\n").map((x) => x.trim()).filter(Boolean).map((title) => ({ title })),
      });
      toast.success("Template created");
      setOpen(false);
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/office/templates/${t.template_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="templates-page">
      <SectionTitle
        right={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setOpen(true)} data-testid="new-template-button">
              <Plus className="w-4 h-4 mr-1" /> New Template</Button>
            <DialogContent className="bg-card border-border max-w-xl" data-testid="template-dialog">
              <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Task Template</DialogTitle>
                <DialogDescription className="sr-only">Create a reusable task template with checklist and subtasks</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Template Name *</Label>
                    <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="tpl-name" placeholder="Customer Complaint" /></div>
                  <div><Label className="text-xs">Task Title Format *</Label>
                    <Input value={f.title_format} onChange={(e) => set("title_format", e.target.value)} data-testid="tpl-title" placeholder="Handle customer complaint" /></div>
                  <div><Label className="text-xs">Department</Label>
                    <Select value={f.department} onValueChange={(v) => set("department", v)}>
                      <SelectTrigger data-testid="tpl-department"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Default Priority</Label>
                    <Select value={f.default_priority} onValueChange={(v) => set("default_priority", v)}>
                      <SelectTrigger data-testid="tpl-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{(meta?.priorities || []).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div><Label className="text-xs">Description</Label>
                  <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="tpl-description" /></div>
                <div><Label className="text-xs">Checklist items (one per line)</Label>
                  <Textarea rows={4} value={f.checklist} onChange={(e) => set("checklist", e.target.value)} data-testid="tpl-checklist"
                    placeholder={"Contact customer\nCollect issue details\nAnalyse root cause\nClose complaint"} /></div>
                <div><Label className="text-xs">Subtasks (one per line)</Label>
                  <Textarea rows={3} value={f.subtasks} onChange={(e) => set("subtasks", e.target.value)} data-testid="tpl-subtasks"
                    placeholder={"Prepare corrective action\nObtain management review"} /></div>
                <Button className="w-full bg-primary text-primary-foreground" onClick={create} data-testid="tpl-create-submit">Create Template</Button>
              </div>
            </DialogContent>
          </Dialog>)}>
        Task Templates
      </SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {rows.map((t) => (
          <div key={t.template_id} className="bg-card border border-border rounded-lg p-4 space-y-1.5" data-testid={`template-${t.template_id}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-semibold text-sm"><LayoutTemplate className="w-4 h-4 text-primary" />{t.name}</span>
              {canManage && (
                <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid={`template-delete-${t.template_id}`}>
                  <Trash2 className="w-4 h-4 text-signal-red" /></Button>)}
            </div>
            <p className="text-xs text-muted-foreground">"{t.title_format}" · {t.department} · <span className="uppercase">{t.default_priority}</span></p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {(t.checklist || []).length} checklist · {(t.subtasks || []).length} subtasks · {t.template_id}</p>
          </div>))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No templates yet. Templates prefill checklist, subtasks and collaborators when creating tasks.</p>}
      </div>
    </div>
  );
}
